import React, { useEffect, useState } from "react";
import { Download, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

/**
 * PWA "Add to Home Screen" prompt. Appears bottom-right after user has
 * used the site for at least 20 seconds and hasn't dismissed it before.
 */
export default function InstallPrompt() {
  const { lang } = useLang();
  const [deferred, setDeferred] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Was this already dismissed by user?
    if (localStorage.getItem("bm_pwa_dismissed") === "1") return;
    // Already installed?
    if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return;

    const handler = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Show only after 20s to avoid interrupting first impression
      setTimeout(() => setVisible(true), 20000);
    };
    window.addEventListener("beforeinstallprompt", handler);
    return () => window.removeEventListener("beforeinstallprompt", handler);
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    const { outcome } = await deferred.userChoice;
    if (outcome === "accepted") {
      setVisible(false);
    }
    setDeferred(null);
  };

  const dismiss = () => {
    setVisible(false);
    localStorage.setItem("bm_pwa_dismissed", "1");
  };

  if (!visible || !deferred) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-50 max-w-sm bg-primary text-primary-foreground rounded-xl shadow-2xl p-4 border-2 border-secondary animate-in slide-in-from-bottom-4"
      data-testid="pwa-install-prompt"
    >
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 opacity-70 hover:opacity-100"
        data-testid="pwa-dismiss-btn"
        aria-label="Dismiss"
      >
        <X size={14} />
      </button>
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-secondary text-secondary-foreground grid place-items-center shrink-0">
          <Download size={18} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">
            {lang === "hi" ? "BhoomiMitra install karein" : "Install BhoomiMitra"}
          </div>
          <p className="text-xs opacity-90 mt-0.5 leading-snug">
            {lang === "hi"
              ? "App jaisa fast experience — bina Play Store ke, phone home screen par."
              : "App-like fast experience — no Play Store, directly on your home screen."}
          </p>
          <button
            onClick={install}
            className="mt-2 text-xs font-semibold bg-secondary text-secondary-foreground px-3 py-1.5 rounded-full hover:opacity-90"
            data-testid="pwa-install-btn"
          >
            {lang === "hi" ? "Install" : "Install"} →
          </button>
        </div>
      </div>
    </div>
  );
}
